import { getPlayer } from "@/api/privateRequests/players/getPlayer";
import { BaseDropdownOption } from "@/components/dropdowns/BaseDropdown";
import SimpleDropdown from "@/components/dropdowns/SimpleDropdown/SimpleDropdown";
import FC24Player from "@/components/molecules/Player/FC24/FC24Player";
import {
  MetaRatingIcon,
  checkMark,
  exclamationMarkIcon,
} from "@/designSystemIcons";
import { uniquePositionScores } from "@/functions/getFormation";
import { cn } from "@/lib/utils";
import { DetailedPlayerContext } from "@/pages/DetailedPlayer/DetailedPlayer";
import MetaRatingCalculations from "@/pages/MetaRatingLandingPage/components/MetaRatingCalculations";
import MetaRatingFilterBar from "@/pages/MetaRatingLandingPage/components/MetaRatingFilterBar";
import MetaRatingTiers from "@/pages/MetaRatingLandingPage/components/MetaRatingTiers";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { PlayerRole, playersSelector } from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

const positiveCheckMark = checkMark("#00E46B");

/** Keys under `metaRating:comparison.notes`; the table below is built at import, so it is translated at render. */
type ComparisonNote =
  | "greatPlayStyles"
  | "insideForwardPlusPlus"
  | "fiveStarFiveStar"
  | "veryHighPace"
  | "lengthy"
  | "noPlayStylesPlus"
  | "goodFinishing"
  | "veryLowPace"
  | "badPlayStylesPlus";

type BasePlayer = {
  id: number;
  role: number;
  notes: { note: ComparisonNote; positve: boolean }[];
};

export type MetaRatingLandingPagePlayer = DetailedPlayerContext & {
  notes: { note: ComparisonNote; positve: boolean }[];
};
export interface MetaRatingComparisonsProp {
  slots?: MetaRatingLandingPagePlayer[];
  topPlayStylesPlus?: number[];
  topStats?: string[];
  topAccelerates?: string[];
  rolesByPosition?: Record<string, PlayerRole[]>;
  selectedPosition?: BaseDropdownOption;
  selectedRole?: BaseDropdownOption;
  setSelectedPosition?: (position: BaseDropdownOption) => void;
  setSelectedRole?: (role: BaseDropdownOption) => void;
}

function MetaRatingComparisonTouch({
  slots,
  topPlayStylesPlus,
  topStats,
  topAccelerates,
  rolesByPosition,
  selectedPosition,
  selectedRole,
  setSelectedPosition,
  setSelectedRole,
}: MetaRatingComparisonsProp) {
  const { t } = useTranslation("metaRating");
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  return (
    <div className="relative overflow-hidden bg-panel-base">
      <div className="flex justify-center gap-4 mobile:gap-16 pt-8 pb-4 min-h-[320px]">
        {slots.map((player, i) => (
          <div key={i} className="flex flex-col items-center mt-8">
            {player && (
              <>
                <div className="w-full scale-160 translate-x-12 mobile:translate-x-6">
                  <FC24Player
                    chemStyleId={0}
                    isSbcPlayer={false}
                    isObjectivePlayer={false}
                    untradeable={false}
                    isMobile={isMobile}
                    hidePrice={true}
                    {...player}
                  />
                </div>
                {/* {player && (
            )} */}
                <div className="absolute top-16 ml-24 mobile:ml-34">
                  <div className="w-12.5 gap-0.5 rounded flex items-center justify-center bg-panel-middleground text-white text-sm font-semibold">
                    {player?.metaRating?.toFixed(1) ?? "-"}
                    <MetaRatingIcon
                      height={"12"}
                      width={"12"}
                      fill={"#D4D8DE"}
                    />
                  </div>
                </div>

                <div className="px-4 pb-2 pt-0 text-sm text-dark-secondary mt-12">
                  <div className="flex items-center gap-1">
                    <div
                      className={cn(
                        "w-4",
                        player.notes[0].positve && "-ml-0.5 mr-0.5",
                      )}
                    >
                      {player.notes[0].positve
                        ? positiveCheckMark
                        : exclamationMarkIcon}
                    </div>
                    <span className="text-xs break-words">
                      {t(`comparison.notes.${player.notes[0].note}` as const)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div
                      className={cn(
                        "w-4",
                        player.notes[1].positve && "-ml-0.5 mr-0.5",
                      )}
                    >
                      {player.notes[1].positve
                        ? positiveCheckMark
                        : exclamationMarkIcon}
                    </div>
                    <span className="text-xs break-words">
                      {t(`comparison.notes.${player.notes[1].note}` as const)}
                    </span>
                  </div>
                  {player.notes[2] && (
                    <div className="flex items-center gap-1">
                      <div
                        className={cn(
                          "w-4",
                          player.notes[2].positve && "-ml-0.5 mr-0.5",
                        )}
                      >
                        {player.notes[2].positve
                          ? positiveCheckMark
                          : exclamationMarkIcon}
                      </div>
                      <span className="text-xs break-words">
                        {t(`comparison.notes.${player.notes[2].note}` as const)}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="px-4 mb-8">
        <label className="text-dark-primary text-xs font-bold">
          {t("comparison.title")}
        </label>
        <span className="block text-dark-tertiary text-xs">
          {t("comparison.description")}
        </span>
      </div>

      <div className="px-4 py-8 bg-panel-background">
        <MetaRatingFilterBar
          rolesByPosition={rolesByPosition}
          selectedPosition={selectedPosition}
          selectedRole={selectedRole}
          setSelectedPosition={setSelectedPosition}
          setSelectedRole={setSelectedRole}
        />
        <MetaRatingCalculations
          topPlayStylesPlus={topPlayStylesPlus}
          topStats={topStats}
          topAccelerates={topAccelerates}
        />
      </div>

      <MetaRatingTiers />
    </div>
  );
}

function MetaRatingComparisonDesktop({
  slots,
  topPlayStylesPlus,
  topStats,
  topAccelerates,
  rolesByPosition,
  selectedPosition,
  selectedRole,
  setSelectedPosition,
  setSelectedRole,
}: MetaRatingComparisonsProp) {
  const { t } = useTranslation("metaRating");
  const { positions: positionLabels } = useSelector(contentLabelsSelector);
  const positionDropdownOptions =
    Object.keys(rolesByPosition).length > 0
      ? Object.keys(rolesByPosition)
          ?.sort((a, b) => uniquePositionScores[b] - uniquePositionScores[a])
          ?.map((position) => ({
            label: positionLabels[position]?.name ?? position,
            value: position,
          }))
      : [];

  const roleDropdownOptions =
    Object.keys(rolesByPosition).length > 0 && selectedPosition?.value
      ? rolesByPosition[selectedPosition.value]?.map((role) => ({
          label: role.name,
          value: role.id.toString(),
        }))
      : [];

  return (
    <div className="relative overflow-hidden px-20 py-10">
      <div className="absolute inset-0 -top-[600px] bg-gradient-meta-ratings-page opacity-60" />
      <div className="relative">
        <div className="mb-5 px-6">
          <label className="text-dark-primary text-base font-bold">
            {t("comparison.title")}
          </label>
          <span className="block text-dark-tertiary text-sm max-w-[394px]">
            {t("comparison.description")}
          </span>
        </div>
        <div className="mt-10 flex gap-4 h-[325px]">
          {slots.map(
            (player, i) =>
              player && (
                <div key={i} className="flex w-1/3 flex-col items-center">
                  <div className="flex items-center justify-center w-[230px] scale-140 mb-8">
                    <FC24Player
                      chemStyleId={0}
                      isSbcPlayer={false}
                      isObjectivePlayer={false}
                      untradeable={false}
                      isMobile={false}
                      hidePrice={true}
                      {...player}
                    />
                  </div>
                  <div className="flex w-full items-stretch justify-center">
                    <div className="flex items-center gap-1 px-4 py-2 bg-white/5 text-xl rounded-l-xl shadow-lg">
                      <span className="text-2xl font-semibold">
                        {player?.metaRating?.toFixed(1) ?? "-"}
                      </span>
                      <span>
                        <MetaRatingIcon fill="var(--color-dark-tertiary)" />
                      </span>
                    </div>
                    <div className="flex flex-col bg-panel-middleground rounded-r-xl p-2 text-dark-secondary text-xs shadow-lg">
                      <div className="flex items-center gap-1">
                        <div
                          className={cn(
                            "w-4",
                            player.notes[0].positve && "-ml-0.5",
                          )}
                        >
                          {player.notes[0].positve
                            ? positiveCheckMark
                            : exclamationMarkIcon}
                        </div>
                        <span className="break-words">
                          {t(
                            `comparison.notes.${player.notes[0].note}` as const,
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div
                          className={cn(
                            "w-4",
                            player.notes[1].positve && "-ml-0.5",
                          )}
                        >
                          {player.notes[1].positve
                            ? positiveCheckMark
                            : exclamationMarkIcon}
                        </div>
                        <span className="break-words">
                          {t(
                            `comparison.notes.${player.notes[1].note}` as const,
                          )}
                        </span>
                      </div>
                      {player.notes[2] && (
                        <div className="flex items-center gap-1">
                          <div
                            className={cn(
                              "w-4",
                              player.notes[2].positve && "-ml-0.5",
                            )}
                          >
                            {player.notes[2].positve
                              ? positiveCheckMark
                              : exclamationMarkIcon}
                          </div>
                          <span className="break-words">
                            {t(
                              `comparison.notes.${player.notes[2].note}` as const,
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ),
          )}
        </div>

        <div className="mt-14 rounded-xl p-6 shadow-lg bg-panel-background">
          <div className="flex flex-col">
            <label className="text-dark-primary text-base font-bold">
              {t("calc.title")}
            </label>
            <span className="text-dark-tertiary text-sm">
              {t("calc.description")}
            </span>
          </div>

          <div className="mt-4">
            <div className="flex gap-3 touch-device:text-sm">
              <SimpleDropdown
                variant="tertiary"
                options={positionDropdownOptions}
                selectedOption={
                  positionDropdownOptions.find(
                    (o) => o.value === selectedPosition?.value,
                  ) ?? selectedPosition
                }
                onChange={setSelectedPosition}
                className="min-w-16"
                large
                align="left"
              />
              <SimpleDropdown
                variant="tertiary"
                options={roleDropdownOptions}
                selectedOption={
                  roleDropdownOptions.find(
                    (o) => o.value === selectedRole?.value,
                  ) ?? selectedRole
                }
                onChange={setSelectedRole}
                className="min-w-24"
                large
                align="left"
              />
            </div>
          </div>

          <div className="mt-6 flex gap-6">
            <MetaRatingCalculations
              topPlayStylesPlus={topPlayStylesPlus}
              topStats={topStats}
              topAccelerates={topAccelerates}
            />
          </div>
        </div>

        <MetaRatingTiers />
      </div>
    </div>
  );
}

/* 
Inside Forward - RW
231747 - Dembele - Great PlayStyles, Inside Forward++, 5* 5*
Advanced Forward - ST
188545 - Lewandowski - Very Low Pace, Bad PS+
Deep Lying Playmaker - CDM
264453 - Van de Ven - Very High Pace, Lengthy, No PS+

*/
const BASE_PLAYERS: BasePlayer[] = [
  {
    id: 231443,
    role: 36, // DEMBELE
    notes: [
      { note: "greatPlayStyles", positve: true },
      { note: "insideForwardPlusPlus", positve: true },
      { note: "fiveStarFiveStar", positve: true },
    ],
  },
  {
    id: 264453,
    role: 11, // VAN DE VEN
    notes: [
      { note: "veryHighPace", positve: true },
      { note: "lengthy", positve: true },
      { note: "noPlayStylesPlus", positve: false },
    ],
  },
  {
    id: 188545,
    role: 41, // LEWANDOWSKI
    notes: [
      { note: "goodFinishing", positve: true },
      { note: "veryLowPace", positve: false },
      { note: "noPlayStylesPlus", positve: false },
    ],
  },
];

const MetaRatingComparison: React.FC = () => {
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const { playerRoles } = useSelector(playersSelector);
  const [players, setPlayers] = useState<MetaRatingLandingPagePlayer[]>([]);

  useEffect(() => {
    const fetchPlayers = async () => {
      const returnedPlayers: MetaRatingLandingPagePlayer[] = await Promise.all(
        BASE_PLAYERS.map(async (player_id_and_role) => {
          const player = await getPlayer({
            resourceId: player_id_and_role.id,
            playerRoleId: player_id_and_role.role,
            backendPlayerType: "base",
          });
          return {
            ...player,
            resourceId: player_id_and_role.id,
            notes: player_id_and_role.notes,
            metaRating:
              player.metaRatings.find(
                (metaRating) =>
                  metaRating.playerRoleId === player_id_and_role.role &&
                  metaRating.chemistry === 0,
              )?.metaRating ?? 0,
          };
        }),
      );

      setPlayers(returnedPlayers);
    };
    fetchPlayers();
  }, []);

  const rolesByPosition = useMemo(
    () =>
      playerRoles.reduce(
        (acc, role) => {
          // Use the position field as the key
          const position = role.position;
          if (!acc[position]) {
            acc[position] = [];
          }
          acc[position].push(role);
          return acc;
        },
        {} as Record<string, typeof playerRoles>,
      ),
    [playerRoles],
  );

  const [selectedPosition, setSelectedPosition] = useState<BaseDropdownOption>({
    label: "ST",
    value: "ST",
  });
  const [selectedRole, setSelectedRole] = useState<BaseDropdownOption>({
    label: "Advanced Forward",
    value: "41",
  });

  const slots = isTouch
    ? [players[0], players[2]]
    : [players[0], players[1], players[2]];

  const getRole = (roleId: string) => {
    return playerRoles.find((role) => role.id === +roleId);
  };
  const handleSetSelectedPosition = (position: BaseDropdownOption) => {
    setSelectedPosition(position);
    if (rolesByPosition?.[position.value]) {
      const rolesForSelectedPosition = rolesByPosition?.[position.value];
      if (
        !rolesForSelectedPosition.find(
          (role) => role.id === +selectedRole.value,
        )
      )
        setSelectedRole({
          label: rolesForSelectedPosition[0].name,
          value: rolesForSelectedPosition[0].id.toString(),
        });
    }
  };

  return isTouch ? (
    <MetaRatingComparisonTouch
      slots={slots}
      topPlayStylesPlus={getRole(selectedRole.value)?.topPlayStylesPlus ?? []}
      topStats={getRole(selectedRole.value)?.topStats ?? []}
      topAccelerates={getRole(selectedRole.value)?.topAccelerates ?? []}
      rolesByPosition={rolesByPosition || {}}
      selectedPosition={selectedPosition}
      selectedRole={selectedRole}
      setSelectedPosition={handleSetSelectedPosition}
      setSelectedRole={setSelectedRole}
    />
  ) : (
    <MetaRatingComparisonDesktop
      slots={slots}
      topPlayStylesPlus={getRole(selectedRole.value)?.topPlayStylesPlus ?? []}
      topStats={getRole(selectedRole.value)?.topStats ?? []}
      topAccelerates={getRole(selectedRole.value)?.topAccelerates ?? []}
      rolesByPosition={rolesByPosition || {}}
      selectedPosition={selectedPosition}
      selectedRole={selectedRole}
      setSelectedPosition={handleSetSelectedPosition}
      setSelectedRole={setSelectedRole}
    />
  );
};

export default MetaRatingComparison;
